import React from 'react';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CalendarDays, ClipboardList, Clock, Sparkles } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { unixToCurrentTimezone } from '../../../../utils/helpers/time';
import type { IParticipantCardData } from '../../interfaces';

export interface IAssessmentCardProps {
  userAssessment: IParticipantCardData;
}

interface IAssessmentStatus {
  normalized: string;
  assessmentExpired: boolean;
  action: string;
  buttonLabel: string;
  chipColor: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  badgeLabel: string;
}

const isPast = (endDate: number): boolean => {
  const currentUnixTimestamp = Math.floor(Date.now() / 1000);
  return endDate < currentUnixTimestamp;
};

const toDay = (unixSeconds: number): string => unixToCurrentTimezone(unixSeconds).split(',')[0];

const STATUS_META: Record<string, Pick<IAssessmentStatus, 'action' | 'buttonLabel' | 'chipColor' | 'badgeLabel'>> = {
  assigned: {
    action: 'Get started with your assessment',
    buttonLabel: 'Start Now',
    chipColor: 'info',
    badgeLabel: 'Active',
  },
  'in progress': {
    action: 'Continue where you left off',
    buttonLabel: 'Continue',
    chipColor: 'info',
    badgeLabel: 'Active',
  },
  expired: {
    action: 'This assessment has expired',
    buttonLabel: 'View Results',
    chipColor: 'default',
    badgeLabel: 'Expired',
  },
  completed: {
    action: 'You have completed this assessment',
    buttonLabel: 'View Results',
    chipColor: 'success',
    badgeLabel: 'Completed',
  },
};

const resolveStatus = (userAssessment: IParticipantCardData): IAssessmentStatus => {
  const apiStatus = (userAssessment.status || '').toLowerCase();
  const assessmentExpired = isPast(userAssessment.endDate);
  const normalized =
    apiStatus === 'completed' || apiStatus === 'expired'
      ? apiStatus
      : assessmentExpired
        ? 'expired'
        : apiStatus;
  const meta = STATUS_META[normalized] ?? {
    action: 'No action required',
    buttonLabel: 'Details',
    chipColor: 'default',
    badgeLabel: normalized,
  };
  return { normalized, assessmentExpired, ...meta };
};

export const AssessmentCard = ({ userAssessment }: IAssessmentCardProps) => {
  const history = useHistory();
  const status = resolveStatus(userAssessment);
  const isCompleted = status.normalized === 'completed';

  const buttonClick = () => {
    history.push('/assigned-assessments');
  };

  const startDateStr = toDay(userAssessment.startDate);

  return (
    <Card
      component="article"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          p: 2.5,
          height: '100%',
          boxSizing: 'border-box',
          '&:last-child': { pb: 2.5 },
        }}
      >
        {/* Header: Icon + Status Chip */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) =>
                isCompleted
                  ? alpha(theme.palette.success.main, 0.1)
                  : alpha(theme.palette.primary.main, 0.1),
              color: isCompleted ? 'success.main' : 'primary.main',
            }}
          >
            {isCompleted ? (
              <ClipboardList size={22} aria-hidden="true" />
            ) : (
              <Sparkles size={22} aria-hidden="true" />
            )}
          </Box>
          <Chip
            label={status.badgeLabel}
            color={status.chipColor}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: '0.72rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          />
        </Stack>

        {/* Title */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              lineHeight: 1.3,
              mb: 0.5,
            }}
          >
            {userAssessment.assessmentTitle}
          </Typography>
        </Box>

        {/* Dates list */}
        <Stack spacing={1.5} sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
              <CalendarDays size={15} aria-hidden="true" />
              <Typography variant="caption" fontWeight={600}>
                {isCompleted ? 'Finished' : 'Assigned'}
              </Typography>
            </Stack>
            <Typography variant="caption" fontWeight={700} color="text.primary">
              {startDateStr}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
              {isCompleted ? (
                <ClipboardList size={15} aria-hidden="true" />
              ) : (
                <Clock size={15} aria-hidden="true" />
              )}
              <Typography variant="caption" fontWeight={600}>
                {isCompleted ? 'Completed' : 'Deadline'}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{
                color: isCompleted
                  ? 'success.main'
                  : status.assessmentExpired
                    ? 'error.main'
                    : 'info.main',
              }}
            >
              {isCompleted ? 'Done' : status.assessmentExpired ? 'Expired' : 'Active'}
            </Typography>
          </Stack>
        </Stack>

        {/* Action Button */}
        {!isCompleted && status.normalized !== 'expired' && (
          <Box sx={{ pt: 1 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={buttonClick}
              disabled={!userAssessment.isActive}
              sx={{
                py: 1,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontSize: '0.78rem',
              }}
            >
              {status.buttonLabel === 'Start Now' ? 'Start Assessment' : 'Continue Session'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
